import React from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { QrCode, ArrowForward, Settings } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { refreshServerQRCodeAsync } from "../../../redux/activity/activityActions";
import { useDispatchWrapper } from "../../../hooks";

interface IWhatsAppQRScanRequiredProps {
  errorType?: "WHATSAPP_NOT_SCANNED" | "WHATSAPP_SERVICE_OFF";
}

export const WhatsAppQRScanRequired: React.FC<IWhatsAppQRScanRequiredProps> = ({
  errorType = "WHATSAPP_NOT_SCANNED",
}) => {
  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const handleQRRedirect = () => {
    navigate("/whatsapp-qr");
  };

  const handleWhatsappServiceOffRedirect = () => {
    navigate("/org-env-config");
  };

  const handleRetry = () => {
    dispatch(refreshServerQRCodeAsync());
  };

  // Dynamic content based on error type
  const isServiceOff = errorType === "WHATSAPP_SERVICE_OFF";
  const title = isServiceOff
    ? "WhatsApp Service Disabled"
    : "WhatsApp Not Connected";
  const description = isServiceOff
    ? "WhatsApp service is currently disabled. Please enable it in the organization settings to start messaging."
    : "You need to scan the QR code to connect WhatsApp and get messages. Please scan the QR code to continue.";
  const buttonText = isServiceOff ? "Enable WhatsApp Service" : "Scan QR Code";
  const buttonIcon = isServiceOff ? <Settings /> : <ArrowForward />;
  const handleClick = isServiceOff
    ? handleWhatsappServiceOffRedirect
    : handleQRRedirect;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        p: 3,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          boxShadow: theme.shadows[4],
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            {/* Dynamic Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {isServiceOff ? (
                <Settings
                  sx={{
                    fontSize: 40,
                    color: "white",
                  }}
                />
              ) : (
                <QrCode
                  sx={{
                    fontSize: 40,
                    color: "white",
                  }}
                />
              )}
            </Box>

            {/* Dynamic Error Message */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                {description}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
              <Button
                variant="outlined"
                onClick={handleRetry}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Retry
              </Button>
              <Button
                variant="contained"
                onClick={handleClick}
                endIcon={buttonIcon}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                {buttonText}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
